import basketCircle from "assets/img/svg/basketCircle";
import ModalBox from "components/Modal";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { PaymentStatus } from "store/types";

import styles from "./style.module.scss";

interface IDeleteTermModal {
    termData: { title: string; beneficiar: string; representative: string } | null;
    resourceData: IBeneficiaryPayment[];
    open: boolean;
    onClose: () => void;
    /**
     * Function to trigger when they confirm deletion.
     */
    onDelete: () => Promise<void>;
}

/**
 * Delete modal for terms with check on payment status.
 */
const DeleteTermModal = ({ termData, resourceData, open, onClose, onDelete }: IDeleteTermModal) => {
    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const handleDelete = async () => {
        setIsLoading(true);
        await onDelete();
        setIsLoading(false);
        onClose();
    };

    /**
     * Returns extra info / action depending on the status of the slice.
     */
    const getSlicePaymentStatus = () => {
        if (!resourceData.length) return <></>;
        let descriptionElement: JSX.Element;
        if (resourceData.length === 1) {
            switch (resourceData[0].status) {
                case PaymentStatus.PAID:
                    descriptionElement = (
                        <span className={styles["advance-options-info"]}>{t("Terms.DeleteModal.Paid")}</span>
                    );
                    break;
                case PaymentStatus.TO_PAY:
                    descriptionElement = (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <span className={styles["advance-options-info"]}>{t("Terms.DeleteModal.ToPay")}</span>
                        </div>
                    );
                    break;
                case PaymentStatus.NOT_PAID:
                    descriptionElement = <></>;
            }
        } else {
            descriptionElement = (
                <span className={styles["advance-options-info"]}>{t("Terms.DeleteModal.AllPayments")}</span>
            );
        }

        return descriptionElement;
    };

    return (
        <ModalBox open={open} onClose={() => onClose()} Icon={basketCircle}>
            <div className={styles["delete-modal-wrapper"]}>
                <div className={styles["delete-title"]}>
                    <h3>
                        {t("DeleteModal.DeleteQuestion")}
                        {t("DeleteModal.Delete", { element: t("DeleteModal.TheTerm") })}
                    </h3>
                    <MessageBlock
                        type="warning"
                        text={t("DeleteModal.AlertMessage")}
                        small
                        style={{ marginTop: "10px" }}
                    />
                </div>
                <div className={styles["delete-description"]}>
                    <span>{t("DeleteModal.TermInfo")}</span>
                    <p>{t("DeleteModal.Title", { titleName: termData?.title ?? "" })}</p>
                    <p>{t("DeleteModal.Beneficiary", { beneficiaryName: termData?.beneficiar ?? "" })}</p>
                    <p>{t("DeleteModal.Representative", { representative: termData?.representative ?? "" })}</p>
                </div>
                <div className={styles["advance-options"]}>{getSlicePaymentStatus()}</div>
                <div className={styles["delete-buttons"]}>
                    <StyledButton onClick={() => onClose()} fullWidth buttonType="secondary">
                        {t("DeleteModal.Cancel")}
                    </StyledButton>
                    <StyledButton
                        onClick={handleDelete}
                        fullWidth
                        buttonType="danger"
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        {!isLoading && t("DeleteModal.ConfirmDelete")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default DeleteTermModal;
